import { useEffect, useMemo, useState } from 'react';
import { dec, formatMoney } from '@makila/fiscal';
import { deliveryDay, duePeriods, isoWeekday, mapsLink, maintenanceOverdue, pendingCharges, periodLabel, recordStop, routeRunsOn, routeStops, type Contract, type LicenceStatus, type Route, type RouteCustomer, type StopStatus } from '@makila/pos-core';
import { ChevronLeft, ChevronRight, LayoutDashboard, MapPin, MessageCircle, Moon, Phone, Plug, RefreshCw, Search, ShoppingCart, Sun, Truck, Undo2, UserPlus, Wallet, X } from 'lucide-react';
import clsx from 'clsx';
import { HardwareDialog } from '../components/HardwareDialog';
import { Rail, RailItem, RailTool } from '../components/Rail';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, identity } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { SyncStatus } from '../lib/sync';
import { useTheme } from '../lib/theme';
import type { Operator } from './Lock';

/** O que esta caixa já fez em cada paragem do dia (guardado no equipamento: a lista sobrevive a fechar a aplicação). */
export interface StopMark { status: StopStatus; reason?: string | null; document?: string; total?: string; at: string }
const kvKey = (day: string) => `stops:${day}`;
export async function markStop(store: IdbTerminalStore, day: string, customerId: string, mark: StopMark) {
  const cur = (await store.kv<Record<string, StopMark>>(kvKey(day))) ?? {};
  await store.setKv(kvKey(day), { ...cur, [customerId]: mark });
}

/** A entrega que se vai cobrar no balcão. */
export interface Delivering { routeId: string | null; day: string; customer: RouteCustomer; items: { productId: string; quantity: string }[] }

interface Props {
  store: IdbTerminalStore; device: DeviceState; licence: LicenceStatus | null; sync: SyncStatus | null; operator: Operator;
  onDeliver: (d: Delivering) => void;
  onCounter: (open?: 'cash' | 'return') => void; onLock: () => void; onSyncNow: () => void; onGestao?: () => void;
}

const WEEKDAY = ['', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo'];
const shift = (day: string, n: number) => new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const dmy = (d: string) => d.split('-').reverse().join('/');
const REASONS = ['Já tinha comprado', 'Sem dinheiro', 'Não precisa esta semana', 'Mudou de fornecedor'];

/**
 * Entregas (água, gás; docs/14 Fase 3): a lista do dia de uma rota, pela ordem
 * de passagem, no telemóvel do motorista e sem rede. Em cada paragem: entregar
 * e cobrar (a venda abre com o cliente e o que ele leva habitualmente), não
 * estava, ou recusou (com o motivo).
 */
export function DeliveriesScreen(p: Props) {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [customers, setCustomers] = useState<RouteCustomer[]>([]);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  // Alugueres por cobrar e manutenções vencidas, por cliente.
  const [rent, setRent] = useState<Map<string, { due: string[]; maintenance: boolean }>>(new Map());
  const [day, setDay] = useState(() => deliveryDay());
  const [routeId, setRouteId] = useState<string | null>(null);
  const [marks, setMarks] = useState<Record<string, StopMark>>({});
  const [refusing, setRefusing] = useState<RouteCustomer | null>(null);
  const [reason, setReason] = useState('');
  const [extra, setExtra] = useState(false);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hwOpen, setHwOpen] = useState(false);
  const [dark, toggleTheme] = useTheme();

  useEffect(() => {
    void (async () => {
      setRoutes((await p.store.catalog('route')).map((i) => i.data as unknown as Route).filter((r) => r.active).sort((a, b) => a.name.localeCompare(b.name, 'pt')));
      setCustomers((await p.store.catalog('customer')).map((i) => i.data as unknown as RouteCustomer).filter((c) => c.active));
      setNames(new Map((await p.store.catalog('product')).map((i) => [i.id, String(i.data.shortName || i.data.name)])));
      const charged = pendingCharges(await p.store.pendingOutbox(5000));
      const m = new Map<string, { due: string[]; maintenance: boolean }>();
      for (const k of (await p.store.catalog('contract')).map((i) => i.data as unknown as Contract).filter((x) => x.active)) {
        const cur = m.get(k.customerId) ?? { due: [], maintenance: false };
        m.set(k.customerId, { due: [...cur.due, ...duePeriods(k, deliveryDay(), charged)], maintenance: cur.maintenance || maintenanceOverdue(k) });
      }
      setRent(m);
    })();
  }, [p.store, p.sync?.lastSyncAt]);
  useEffect(() => { void p.store.kv<Record<string, StopMark>>(kvKey(day)).then((m) => setMarks(m ?? {})); }, [p.store, day, p.sync?.lastSyncAt]);

  // A rota escolhida: a do motorista, senão a primeira que sai neste dia.
  const today = routes.filter((r) => routeRunsOn(r, day) || customers.some((c) => c.routeId === r.id && c.deliveryDays?.includes(isoWeekday(day))));
  const route = routes.find((r) => r.id === routeId) ?? today.find((r) => r.driverId === p.operator.id) ?? today[0] ?? routes[0] ?? null;
  const stops = useMemo(() => (route ? routeStops(route, customers, day) : []), [route, customers, day]);
  // Clientes fora da rota a quem já se entregou hoje (aparecem no fim).
  const offRoute = customers.filter((c) => marks[c.id] && !stops.some((s) => s.id === c.id) && (c.routeId !== route?.id || !route));
  const list = [...stops, ...offRoute];
  const done = list.filter((c) => marks[c.id] && marks[c.id]!.status !== 'absent').length;
  const cashed = list.reduce((s, c) => s.plus(marks[c.id]?.total ?? 0), dec(0));
  const isToday = day === deliveryDay();

  const stop = async (c: RouteCustomer, status: StopStatus, why?: string) => {
    try {
      const rid = c.routeId && c.routeId === route?.id ? route.id : null;
      await recordStop(p.store, identity(p.device), { routeId: rid, customerId: c.id, day, status, reason: why ?? null, operatorId: p.operator.id });
      const mark: StopMark = { status, reason: why ?? null, at: new Date().toISOString() };
      await markStop(p.store, day, c.id, mark);
      setMarks((m) => ({ ...m, [c.id]: mark }));
      setRefusing(null); setReason(''); setError(null);
      p.onSyncNow();
    } catch (e) { setError((e as Error).message); }
  };
  const deliver = (c: RouteCustomer) => p.onDeliver({ routeId: c.routeId && c.routeId === route?.id ? route.id : null, day, customer: c, items: c.usualItems ?? [] });
  const found = q.trim() ? customers.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()) || (c.phone ?? '').includes(q.trim())).slice(0, 8) : [];

  return (
    <div className="flex h-full flex-col-reverse md:flex-row">
      <Rail label="Caixa" footer={<>
        {p.onGestao && <RailItem icon={LayoutDashboard} label="Gestão" kbd="F10" ariaLabel="Abrir a gestão" onClick={p.onGestao} />}
        <RailTool icon={Plug} label="Equipamento" onClick={() => setHwOpen(true)} />
        <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
        <RailTool icon={RefreshCw} label="Sincronizar agora" onClick={p.onSyncNow} />
        <RailTool label="Trocar de operador" onClick={p.onLock}>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-700 text-sm font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
        </RailTool>
      </>}>
        <RailItem icon={Truck} label="Entregas" active badge={stops.length - done || null} />
        <RailItem icon={ShoppingCart} label="Balcão" onClick={() => p.onCounter()} />
        <RailItem icon={Undo2} label="Devolução" kbd="F8" disabled={!p.licence?.sell} onClick={() => p.onCounter('return')} />
        <RailItem icon={Wallet} label="Caixa" kbd="F9" onClick={() => p.onCounter('cash')} />
      </Rail>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 flex-wrap items-center gap-x-3 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
          <div className="min-w-0">
            <div className="truncate font-display text-[17px] font-semibold leading-tight">Entregas</div>
            <div className="truncate text-xs text-muted">{done} de {stops.length} paragens · {formatMoney(cashed.toFixed(2))} MT · {p.operator.name}</div>
          </div>
          <div className="flex items-center gap-1">
            <button aria-label="Dia anterior" onClick={() => setDay(shift(day, -1))} className="grid h-11 w-11 place-items-center rounded-xl hover:bg-surface-2"><ChevronLeft size={20} /></button>
            <button onClick={() => setDay(deliveryDay())} className={clsx('h-11 rounded-xl px-3 text-sm font-semibold', isToday ? 'bg-surface-2' : 'border border-base')}>{isToday ? 'Hoje' : `${WEEKDAY[isoWeekday(day)]}, ${dmy(day)}`}</button>
            <button aria-label="Dia seguinte" onClick={() => setDay(shift(day, 1))} className="grid h-11 w-11 place-items-center rounded-xl hover:bg-surface-2"><ChevronRight size={20} /></button>
          </div>
          <div className="ml-auto"><StatusPill sync={p.sync} /></div>
        </header>
        {routes.length > 0 && (
          <div role="tablist" aria-label="Rotas" className="flex gap-2 overflow-x-auto border-b border-base bg-[var(--surface)] px-4 py-2">
            {routes.map((r) => (
              <button key={r.id} role="tab" aria-selected={r.id === route?.id} onClick={() => setRouteId(r.id)}
                className={clsx('flex h-11 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-semibold', r.id === route?.id ? 'bg-brand-700 text-white' : 'border border-base', !today.includes(r) && r.id !== route?.id && 'opacity-50')}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color ?? 'currentColor' }} /> {r.name}
              </button>
            ))}
          </div>
        )}
        {error && <div role="alert" className="bg-amber-100 px-4 py-2 text-amber-900">{error}</div>}
        <main className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
          <div className="mx-auto max-w-3xl space-y-3">
            {!routes.length ? (
              <div className="surface rounded-2xl p-6 text-center text-muted">Ainda não há rotas. Crie-as na gestão, em <b>Rotas → Rotas</b>, e junte-lhes os clientes.</div>
            ) : route && !stops.length ? (
              <div className="surface rounded-2xl p-6 text-center text-muted">A rota <b>{route.name}</b> não tem paragens {isToday ? 'hoje' : `na ${WEEKDAY[isoWeekday(day)]}`}.</div>
            ) : null}
            {list.map((c, i) => {
              const m = marks[c.id];
              const off = i >= stops.length;
              const usual = (c.usualItems ?? []).map((x) => `${String(x.quantity).replace('.', ',')} × ${names.get(x.productId) ?? 'artigo'}`).join(' · ');
              return (
                <article key={c.id} aria-label={`Paragem ${c.name}`} className={clsx('surface rounded-2xl p-4', m?.status === 'delivered' && 'opacity-70')}>
                  <div className="flex items-start gap-3">
                    <span className={clsx('grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-bold', m?.status === 'delivered' ? 'bg-green-700 text-white' : m ? 'bg-amber-500 text-white' : 'bg-surface-2')}>{off ? '+' : i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2">
                        <h3 className="font-display text-lg font-semibold leading-tight">{c.name}</h3>
                        {m && <span className={clsx('rounded-full px-2 py-0.5 text-xs font-semibold', m.status === 'delivered' ? 'bg-green-100 text-green-800' : m.status === 'absent' ? 'bg-amber-100 text-amber-900' : 'bg-brand-100 text-brand-800')}>
                          {m.status === 'delivered' ? `Entregue${m.document ? ` · ${m.document}` : ''}` : m.status === 'absent' ? 'Não estava' : `Recusou · ${m.reason}`}
                        </span>}
                        {off && <span className="text-xs text-muted">fora da rota</span>}
                      </div>
                      {(c.district || c.landmark || c.address) && <p className="mt-0.5 text-sm text-muted">{[c.district, c.landmark || c.address].filter(Boolean).join(' · ')}</p>}
                      {usual && <p className="mt-1 text-sm">Habitual: <b>{usual}</b></p>}
                      {!!rent.get(c.id)?.due.length && <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">Aluguer por cobrar: <b>{[...new Set(rent.get(c.id)!.due)].sort().map(periodLabel).join(', ')}</b></p>}
                      {rent.get(c.id)?.maintenance && <p className="mt-1 text-sm text-brand-700">Manutenção do equipamento vencida</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {m?.status !== 'delivered' && <>
                      <button onClick={() => deliver(c)} disabled={!p.licence?.sell} className="flex h-12 basis-full items-center justify-center gap-2 rounded-xl bg-brand-700 px-4 font-bold text-white disabled:opacity-40 sm:basis-auto sm:flex-1">
                        <ShoppingCart size={18} /> Entregar e cobrar
                      </button>
                      {!m && <button onClick={() => void stop(c, 'absent')} className="h-12 flex-1 rounded-xl border border-base px-4 font-semibold sm:flex-none">Não estava</button>}
                      {m?.status !== 'refused' && <button onClick={() => { setRefusing(c); setReason(''); }} className="h-12 flex-1 rounded-xl border border-base px-4 font-semibold sm:flex-none">Recusou</button>}
                    </>}
                    {c.phone && <a aria-label={`Ligar a ${c.name}`} href={`tel:${c.phone}`} className="grid h-12 w-12 place-items-center rounded-xl border border-base"><Phone size={18} /></a>}
                    {c.phone && <a aria-label={`WhatsApp a ${c.name}`} target="_blank" rel="noreferrer" href={`https://wa.me/${c.phone.replace(/\D/g, '').replace(/^(8\d{8})$/, '258$1')}`} className="grid h-12 w-12 place-items-center rounded-xl border border-base text-green-700"><MessageCircle size={18} /></a>}
                    {c.gps && <a aria-label={`Mapa de ${c.name}`} target="_blank" rel="noreferrer" href={mapsLink(c.gps)} className="grid h-12 w-12 place-items-center rounded-xl border border-base"><MapPin size={18} /></a>}
                  </div>
                  {refusing?.id === c.id && (
                    <div className="mt-3 space-y-2 rounded-xl bg-surface-2 p-3">
                      <div className="flex flex-wrap gap-2">{REASONS.map((r) => <button key={r} onClick={() => setReason(r)} className={clsx('h-10 rounded-full px-3 text-sm', reason === r ? 'bg-brand-700 text-white' : 'border border-base bg-[var(--surface)]')}>{r}</button>)}</div>
                      <input aria-label="Porque recusou" placeholder="Porque recusou?" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} className="h-12 w-full rounded-xl border border-base bg-[var(--surface)] px-3" />
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setRefusing(null)} className="h-12 rounded-xl border border-base font-medium">Cancelar</button>
                        <button disabled={!reason.trim()} onClick={() => void stop(c, 'refused', reason)} className="h-12 rounded-xl bg-brand-700 font-semibold text-white disabled:opacity-40">Registar recusa</button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
            {routes.length > 0 && (!extra ? (
              <button onClick={() => setExtra(true)} className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-base font-semibold text-muted"><UserPlus size={20} /> Entregar a um cliente fora da rota</button>
            ) : (
              <div className="surface space-y-2 rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} />
                    <input autoFocus aria-label="Procurar cliente" placeholder="Nome ou telefone" value={q} onChange={(e) => setQ(e.target.value)} className="h-12 w-full rounded-xl border border-base bg-[var(--surface)] pl-10 pr-3" />
                  </div>
                  <button aria-label="Fechar" onClick={() => { setExtra(false); setQ(''); }} className="grid h-12 w-12 place-items-center rounded-xl border border-base"><X size={18} /></button>
                </div>
                {found.map((c) => (
                  <button key={c.id} onClick={() => { setExtra(false); setQ(''); deliver(c); }} className="flex h-12 w-full items-center justify-between rounded-xl px-3 text-left hover:bg-surface-2">
                    <span className="font-medium">{c.name}</span><span className="text-sm text-muted">{c.district ?? c.phone ?? ''}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </main>
      </div>
      {hwOpen && <HardwareDialog onClose={() => setHwOpen(false)} />}
    </div>
  );
}
